import { setProperties } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type RSVP from 'rsvp';
import { allSettled } from 'rsvp';

import type { APIData, Coredata, RequestError, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { deleteKeys } from 'webapp/utils/common';

import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type UserRecord from 'webapp/models/user';
import DataLoaderBase from 'webapp/data-loader/base';

import { AppType as VersionCheckerAppType } from 'webapp/services/version-checker';

export default class DataLoaderMobile extends DataLoaderBase {
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('connect') declare connectService: Service.Connect;
	@service('logger') declare loggerService: Service.Logger;
	@service('socket') declare socketService: Service.Socket;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override initialRequestsDone = false;
	initialRequestsPromise: Promise<void>;

	constructor() {
		super();
		this.initialRequestsDone = false;
		this.initialRequestsPromise = new Promise((resolve) => {
			// keep pending until we resolve it with the fn
			this.resolveInitialRequestsPromiseFn = resolve;
		});
	}

	loadGlobals(allData: Coredata.Mobile): void {
		// push faked user record, so we don't have to get /d/all
		const userData = {
			id: allData.userId,
			name: allData.userName,
			email: allData.userEmail,
			level: allData.userLevel,
			timezone: allData.userTimezone,
		};
		this.storeService.pushPayload({ users: [userData] });

		const activeUserRecord = this.storeService.peekRecord(
			ModelName.USER,
			userData.id,
		) as UserRecord;

		setProperties(this.currentUserService, {
			model: this.mmUsersService.createModelFromRecord(activeUserRecord),
			loggedInAsPowerUser: allData.loggedInAsPowerUser,
			isPowerUser: allData.isPowerUser,
			isPlatformAdmin: allData.isPlatformAdmin,
			isOnboarded: true,
		});

		deleteKeys(
			allData,
			'userId',
			'userName',
			'userEmail',
			'userLevel',
			'userTimezone',
			'loggedInAsPowerUser',
			'isPowerUser',
			'isPlatformAdmin',
		);

		// save tokens
		this.currentUserService.userDataToken = allData.userDataToken;
		this.currentUserService.accessToken = allData.accessToken;
		this.currentUserService.accessTokenExpiry = allData.accessTokenExpiry;
		this.currentUserService.initRefreshingTokens();

		deleteKeys(allData, 'userDataToken', 'accessToken', 'accessTokenExpiry');

		// other data that we need to save as we can't get it anywhere else
		this.appNotificationsService.skipYearlyUpgradeNotification = Infinity; // skip forever and ever

		this.requestService.apiUrl = allData.apiUrl;
		this.requestService.connectApiUrl = allData.connectUrl;
		this.requestService.debugParam = allData.debugParam;
		this.requestService.pusherEnvId = allData.pusherEnvId;
		this.requestService.pusherKey = allData.pusherKey;
		this.requestService.pusherCluster = allData.pusherCluster;
		this.requestService.amaBackendUrl = allData.amaBackendUrl;
		this.requestService.websiteUrl = allData.websiteUrl;
		this.requestService.aiBffUrl = allData.aiBffUrl;
		this.requestService.authApiUrl = allData.authApiUrl;
		this.requestService.authGoogleClientId = allData.authGoogleClientId;
		this.requestService.datasetsBackendUrl = allData.datasetsBackendUrl;
		this.requestService.datasetsPusherKey = allData.datasetsPusherKey;
		this.requestService.datasetsPusherCluster = allData.datasetsPusherCluster;
		this.requestService.datasetsSchemaExplorerUrl = allData.datasetsSchemaExplorerUrl;

		deleteKeys(
			allData,
			'apiUrl',
			'connectUrl',
			'debugParam',
			'pusherEnvId',
			'pusherKey',
			'pusherCluster',
			'amaBackendUrl',
			'websiteUrl',
			'aiBffUrl',
			'authApiUrl',
			'datasetsBackendUrl',
			'datasetsPusherKey',
			'datasetsPusherCluster',
			'datasetsSchemaExplorerUrl',
		);

		this.connectService.initialize();

		this.versionCheckerService.isEnabled = false;
		this.versionCheckerService.version = allData.version;
		this.versionCheckerService.appType = VersionCheckerAppType.GENERAL;

		deleteKeys(allData, 'version');

		DateRange.setFormulas(allData);

		DateRange.createPredefinedRanges();
		DateRange.createPredefinedGranulations();
		DsCompare.createPredefinedCompares();

		deleteKeys(allData, 'cdrRanges', 'cdrGranulations');

		MetricLimitations.parseBaseConstraints(allData.cdrLimitationsBase.ranges);

		deleteKeys(allData, 'cdrLimitationsBase');

		// we have local config available for board colors
		deleteKeys(allData, 'boardColors');

		// all the rest: constants
		const constants = allData;
		this.constantsService.update(constants);
	}

	doInitialRequests(): Promise<void> {
		void this.loadData().then(() => {
			this.markInitialRequestsPromiseAsResolved();
		});

		return Promise.resolve(); // non-blocking
	}

	afterInitialRequests(_initialRouteTo: string): Promise<void> {
		return Promise.resolve();
	}

	private loadData(): Promise<void> {
		const accountInfoPromise = this.requestService
			.api<APIData.Account.AccountResponse>('GET', '/account')
			.then((data) => {
				this.accountService.update(data.account);
				this.loggerService.spaceId = data.account.id;

				// start socket
				this.socketService.subscribeToAppEvents();
				this.socketService.subscribeToAiConnection();

				// analytics
				this.analyticsService.initializeFromEnv();
			});

		// must not process most of the requests before source types are loaded
		const sourceTypesPromise = this.mmSourceTypesService.requestSourceTypes(
			() => accountInfoPromise,
			true,
		);

		const promises: Promise<unknown>[] = [];

		promises.push(accountInfoPromise);

		promises.push(sourceTypesPromise);

		promises.push(this.requestService.storeFindAll(ModelName.SOURCE));

		return new Promise((resolve /*, reject*/) => {
			allSettled(promises).then((statusArray) => {
				const isAllSuccess = !statusArray
					.map((item) => item.state)
					.includes('rejected' as RSVP.State);
				if (isAllSuccess) {
					resolve();
				} else {
					statusArray
						// all of these are request service requests, which reject with a RequestError
						.filter(
							(item): item is RSVP.Rejected<RequestError> =>
								item.state === ('rejected' as RSVP.State),
						)
						.forEach((status) => {
							this.requestService.handleGenericThrownError(status.reason);
						});
					// reject();
				}
			});
		});
	}
}
